import {GameEvent} from "../../api/ws/events";
import {ConnectionStatus, User} from "./lobby";

export enum GameState {
    STARTED = 'STARTED',
    INITIAL_CARD_REVEAL = 'INITIAL_CARD_REVEAL',
    MAIN_PLAY = 'MAIN_PLAY',
    RESULTS_REVEAL = 'RESULTS_REVEAL',
    FINISHED = 'FINISHED',
    UNFINISHED = 'UNFINISHED',
}

export function isGameAvailable(gameState?: GameState): boolean {
    if (!gameState) {
        return false;
    }

    return gameState !== GameState.FINISHED && gameState !== GameState.UNFINISHED;
}

export enum TurnState {
    PREPARING = 'PREPARING',
    CARD_TAKE = 'CARD_TAKE',
    USER_ACTIONS = 'USER_ACTIONS',
    FINISHING = 'FINISHING',
}

export enum CardType {
    MINUS_ONE = 'MINUS_ONE',
    ZERO = 'ZERO',
    ONE = 'ONE',
    TWO = 'TWO',
    THREE = 'THREE',
    FOUR = 'FOUR',
    FIVE = 'FIVE',
    SIX = 'SIX',
    SEVEN = 'SEVEN',
    EIGHT = 'EIGHT',
    NINE = 'NINE',
    ELEVEN = 'ELEVEN',
    CIRCLE = 'CIRCLE',
    TRIANGLE = 'TRIANGLE',
    SQUARE = 'SQUARE',
    UNKNOWN = 'UNKNOWN',
    CARD_BACK = 'CARD_BACK',
    EMPTY = 'EMPTY'
}

export enum UserAction {
    CHOOSE_CARD = 'CHOOSE_CARD',
    TOSS_CARD = 'TOSS_CARD',
    GIVE_CARD_CHOOSE_CARD = 'GIVE_CARD_CHOOSE_CARD',
    PEEK_OWN_CARD = 'PEEK_OWN_CARD',
    PEEK_OTHERS_CARD = 'PEEK_OTHERS_CARD',
    FORCE_CARD_TAKE = 'FORCE_CARD_TAKE',
    CIRCLE_CHOOSE_FIRST_CARD = 'CIRCLE_CHOOSE_FIRST_CARD',
    CIRCLE_CHOOSE_SECOND_CARD_SWAP = 'CIRCLE_CHOOSE_SECOND_CARD_SWAP',
    TRIANGLE_CHOOSE_PEEK_FIRST_CARD = 'TRIANGLE_CHOOSE_PEEK_FIRST_CARD',
    TRIANGLE_CHOOSE_SECOND_CARD_SWAP = 'TRIANGLE_CHOOSE_SECOND_CARD_SWAP',
    SQUARE_CHOOSE_PEEK_FIRST_CARD = 'SQUARE_CHOOSE_PEEK_FIRST_CARD',
    SQUARE_CHOOSE_PEEK_SECOND_CARD = 'SQUARE_CHOOSE_PEEK_SECOND_CARD',
    SQUARE_CARD_SWAP = 'SQUARE_CARD_SWAP',
    KNOCK = 'KNOCK',
}

export enum CardActionType {
    MOVED,
    SWAPPED,
    SELECTED,
    NOTHING
}

export const USER_ACTION_LABELS = {
    [UserAction.CHOOSE_CARD]: 'Choose Card to play',
    [UserAction.TOSS_CARD]: 'Toss same card?',
    [UserAction.GIVE_CARD_CHOOSE_CARD]: 'Give up your card',
    [UserAction.PEEK_OWN_CARD]: 'Peek your card',
    [UserAction.PEEK_OTHERS_CARD]: 'Peek other player card',
    [UserAction.FORCE_CARD_TAKE]: 'Choose player to take a deck card',
    [UserAction.CIRCLE_CHOOSE_FIRST_CARD]: 'Choose 1st card to swap',
    [UserAction.CIRCLE_CHOOSE_SECOND_CARD_SWAP]: 'Choose 2nd card to swap',
    [UserAction.TRIANGLE_CHOOSE_PEEK_FIRST_CARD]: 'Peek 1st card to swap',
    [UserAction.TRIANGLE_CHOOSE_SECOND_CARD_SWAP]: 'Choose 2nd card to swap',
    [UserAction.SQUARE_CHOOSE_PEEK_FIRST_CARD]: 'Choose 1st card to swap',
    [UserAction.SQUARE_CHOOSE_PEEK_SECOND_CARD]: 'Peek 2nd card and peek both',
    [UserAction.SQUARE_CARD_SWAP]: 'Swap',
    [UserAction.KNOCK]: 'Knock',
}

export const USER_ACTION_CARD_ACTION = {
    [UserAction.CHOOSE_CARD]: CardActionType.MOVED,
    [UserAction.TOSS_CARD]: CardActionType.MOVED,
    [UserAction.GIVE_CARD_CHOOSE_CARD]: CardActionType.MOVED,
    [UserAction.PEEK_OWN_CARD]: CardActionType.SELECTED,
    [UserAction.PEEK_OTHERS_CARD]: CardActionType.SELECTED,
    [UserAction.FORCE_CARD_TAKE]: CardActionType.MOVED,
    [UserAction.CIRCLE_CHOOSE_FIRST_CARD]: CardActionType.SELECTED,
    [UserAction.CIRCLE_CHOOSE_SECOND_CARD_SWAP]: CardActionType.SWAPPED,
    [UserAction.TRIANGLE_CHOOSE_PEEK_FIRST_CARD]: CardActionType.SELECTED,
    [UserAction.TRIANGLE_CHOOSE_SECOND_CARD_SWAP]: CardActionType.SWAPPED,
    [UserAction.SQUARE_CHOOSE_PEEK_FIRST_CARD]: CardActionType.SELECTED,
    [UserAction.SQUARE_CHOOSE_PEEK_SECOND_CARD]: CardActionType.SELECTED,
    [UserAction.SQUARE_CARD_SWAP]: CardActionType.SWAPPED,
    [UserAction.KNOCK]: CardActionType.NOTHING,
}

export interface GameData {
    turn: number,
    currentPlayerId: number,
    winningPlayerIds: number[],
    lockedPlayerId?: number,
    sameCardTossed?: Card,
    turnState: TurnState,
    lastPlayerAction?: CardActionEvent,
    lastTossCartEvent?: TossCartEvent,
    deckCardsLength: number,
    discardCardsLength: number,
    lastDiscardCard: Card,
    topDeckCard: Card
}

export interface Card {
    id: number,
    type: CardType
}

export interface Game {
    id: number,
    lobbyId: number,
    version: number,
    state: GameState,
    gameData: GameData,
    users: GameUser[],
    created: Date
}

export interface GameUser {
    id: number,
    userId: number,
    gameId: number,
    user: User,
    turnOrder: number,
    gameUserData: GameUserData,
    joined: Date,
}

export interface PlayerCardHolder {
    rows: number,
    cards: PlayerCards
}

interface PlayerCards {
    [key: number]: Card;
}

interface PlayerActions {
    requiredAction?: UserAction;
    optionalAction?: UserAction;
    tossAction?: UserAction;
    tossPlayerId?: number;
}

export interface CardPosition {
    slot: number;
    card: Card;
}

export interface GameUserData {
    connectionStatus: ConnectionStatus,
    cards: PlayerCardHolder,
    actions: PlayerActions,
    points: number,
}

export const CARD_TEXT = {
    [CardType.MINUS_ONE]: '-1',
    [CardType.ZERO]: '0',
    [CardType.ONE]: '1',
    [CardType.TWO]: '2',
    [CardType.THREE]: '3',
    [CardType.FOUR]: '4',
    [CardType.FIVE]: '5',
    [CardType.SIX]: '6',
    [CardType.SEVEN]: '7',
    [CardType.EIGHT]: '8',
    [CardType.NINE]: '9',
    [CardType.ELEVEN]: '11',
    [CardType.CIRCLE]: '◯',
    [CardType.TRIANGLE]: '△',
    [CardType.SQUARE]: '□',
    [CardType.UNKNOWN]: '?',
    [CardType.CARD_BACK]: '',
    [CardType.EMPTY]: ''
}

export const OPPONENT_SEAT_ORDER: { [key: number]: string[] } = {
    [1]: [],
    [2]: ["top"],
    [3]: ["left", "top"],
    [4]: ["left", "top", "right"],
    [5]: ["left", "topLeft", "top", "right"],
    [6]: ["left", "topLeft", "top", "topRight", "right"],
    [7]: ["bottomLeft", "left", "topLeft", "top", "topRight", "right"],
    [8]: ["bottomLeft", "left", "topLeft", "top", "topRight", "right", "bottomRight"],
}

export interface CardActionEvent {
    version: number,
    userAction: UserAction,
    eventInitiatorId: number,
    playerId: number,
    cardSlot: number,

    playerId2?: number,
    cardSlot2?: number,
    swapped?: boolean,
}

export interface TossCartEvent {
    version?: number,
    eventInitiatorId?: number,
    punishCardSlot?: number,
    playerId?: number,
    cardSlot?: number,
    success?: boolean,
}

export function reduceGame(prev: Game | null, event: GameEvent): Game | null {
    if (event.type === 'GAME_STATE') {
        return {...event.game, version: event.version};
    }

    if (!prev) {
        return prev;
    }

    if (event.version <= prev.version) {
        console.warn('Ignore stale game event', event, 'currentVersion = ', prev.version);
        return prev;
    }

    const next: Game = {...prev, version: event.version};

    switch (event.type) {
        case 'PLAYER_HANG': {
            const {playerId} = event;
            return {
                ...next,
                users: next.users.map(u =>
                    u.userId === playerId
                        ? {
                            ...u,
                            gameUserData: {
                                ...u.gameUserData,
                                connectionStatus: ConnectionStatus.HANG,
                            },
                        }
                        : u,
                ),
            };
        }

        case 'PLAYER_BACK': {
            const {playerId} = event;
            return {
                ...next,
                users: next.users.map(u =>
                    u.userId === playerId
                        ? {
                            ...u,
                            gameUserData: {
                                ...u.gameUserData,
                                connectionStatus: ConnectionStatus.ONLINE,
                            },
                        }
                        : u,
                ),
            };
        }

        default: {
            console.warn("Unknown event", event);
            return next;
        }
    }
}
